<template>
  <div class="chart-donut-double">
    <div
      class="chart-donut-double-canvas"
      :style="canvasStyle"
    >
      <svg
        :width="size"
        :height="size"
        :viewBox="`0 0 ${size} ${size}`"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <mask id="mask-outer-cap">
            <rect
              x="0"
              :y="-size"
              :width="size"
              :height="size * 2"
              fill="white"
              :transform="maskOuterTransform"
            />
          </mask>
          <mask id="mask-inner-cap">
            <rect
              x="0"
              :y="-size"
              :width="size"
              :height="size * 2"
              fill="white"
              :transform="maskInnerTransform"
            />
          </mask>
        </defs>
        <g :transform="`translate(${size / 2}, ${size / 2})`">
          <!-- Outer ring (Remote) - background -->
          <circle
            class="ring-background ring-outer-bg"
            :r="outerRadius"
            fill="none"
            :stroke-width="thickness"
          />
          <g
            ref="outerRing"
            class="ring-group ring-group-outer"
          >
            <!-- Outer Ring Start Cap -->
            <rect
              v-if="normalizedOuterValue > 0"
              class="ring-cap-start-outer"
              :x="-2"
              :y="-outerRadius - thickness / 2"
              :width="thickness / 3"
              :height="thickness"
              :rx="4"
              :ry="2"
            />
            <!-- Outer ring (Remote) - value -->
            <circle
              class="ring-value ring-outer"
              :r="outerRadius"
              fill="none"
              :stroke-width="thickness"
              :stroke-dasharray="outerCircumference"
              :stroke-dashoffset="outerOffset"
              :transform="`rotate(-90)`"
            />
            <!-- Outer Ring End Cap -->
            <g
              class="ring-cap-group"
              :style="outerCapStyle"
            >
              <circle
                class="ring-cap-outer"
                :r="thickness / 2"
                :cx="0"
                :cy="-outerRadius"
                :mask="shouldMaskOuter ? 'url(#mask-outer-cap)' : null"
              />
            </g>
          </g>

          <template v-if="innerCircle">
            <!-- Inner ring (Office) - background -->
            <circle
              class="ring-background ring-inner-bg"
              :r="innerRadius"
              fill="none"
              :stroke-width="thickness"
            />
            <g
              ref="innerRing"
              class="ring-group ring-group-inner"
            >
              <!-- Inner Ring Start Cap -->
              <rect
                v-if="normalizedInnerValue > 0"
                class="ring-cap-start-inner"
                :x="-2"
                :y="-innerRadius - thickness / 2"
                :width="thickness / 3"
                :height="thickness"
                :rx="4"
                :ry="2"
              />
              <!-- Inner ring (Office) - value -->
              <circle
                class="ring-value ring-inner"
                :r="innerRadius"
                fill="none"
                :stroke-width="thickness"
                :stroke-dasharray="innerCircumference"
                :stroke-dashoffset="innerOffset"
                :transform="`rotate(-90)`"
              />
              <!-- Inner Ring End Cap -->
              <g
                class="ring-cap-group"
                :style="innerCapStyle"
              >
                <circle
                  class="ring-cap-inner"
                  :r="thickness / 2"
                  :cx="0"
                  :cy="-innerRadius"
                  :mask="shouldMaskInner ? 'url(#mask-inner-cap)' : null"
                />
              </g>
            </g>
          </template>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import D3Tooltip from '~/utils/d3_tooltip';

export default {
  name: 'AppChartDonutDouble',
  props: {
    circles: {
      type: Array,
      required: true,
      validator: (val) =>
        val.length >= 1 &&
        val.length <= 2 &&
        val.every((a) => typeof a.value === 'number' && a.value >= 0 && a.value <= 200),
    },
    size: {
      type: Number,
      default: 145,
    },
    thickness: {
      type: Number,
      default: 12,
    },
    maxPercent: {
      type: Number,
      default: 100,
    },
  },
  data: () => ({
    tooltips: [],
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
      offset: '0, 2',
    }),
  }),
  computed: {
    outerCircle() {
      return this.circles[0] || { value: 0, tooltip: '' };
    },
    outerTooltip() {
      return this.outerCircle?.tooltip;
    },
    innerCircle() {
      return this.circles.length > 1 ? this.circles[1] : null;
    },
    innerTooltip() {
      return this.innerCircle?.tooltip;
    },
    canvasStyle() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
      };
    },
    gap() {
      return this.thickness + 7;
    },
    outerRadius() {
      return (this.size - this.thickness) / 2;
    },
    innerRadius() {
      return this.outerRadius - this.gap;
    },
    outerCircumference() {
      return 2 * Math.PI * this.outerRadius;
    },
    innerCircumference() {
      return 2 * Math.PI * this.innerRadius;
    },
    normalizedOuterValue() {
      return Math.min(Math.max(this.outerCircle.value, 0), this.maxPercent);
    },
    normalizedInnerValue() {
      return Math.min(Math.max(this.innerCircle?.value ?? 0, 0), this.maxPercent);
    },
    outerOffset() {
      const progress = this.normalizedOuterValue / this.maxPercent;

      return this.outerCircumference * (1 - progress);
    },
    innerOffset() {
      const progress = this.normalizedInnerValue / this.maxPercent;

      return this.innerCircumference * (1 - progress);
    },
    outerCapStyle() {
      const rotation = (this.normalizedOuterValue / this.maxPercent) * 360;

      return {
        transform: `rotate(${rotation}deg)`,
        opacity: this.normalizedOuterValue > 0 ? 1 : 0,
      };
    },
    innerCapStyle() {
      const rotation = (this.normalizedInnerValue / this.maxPercent) * 360;

      return {
        transform: `rotate(${rotation}deg)`,
        opacity: this.normalizedInnerValue > 0 ? 1 : 0,
      };
    },
    shouldMaskOuter() {
      return this.normalizedOuterValue <= 10 && this.normalizedOuterValue > 0;
    },
    shouldMaskInner() {
      return this.normalizedInnerValue <= 10 && this.normalizedInnerValue > 0;
    },
    maskOuterTransform() {
      const rotation = (this.normalizedOuterValue / this.maxPercent) * 360;

      return `rotate(-${rotation})`;
    },
    maskInnerTransform() {
      const rotation = (this.normalizedInnerValue / this.maxPercent) * 360;

      return `rotate(-${rotation})`;
    },
  },
  mounted() {
    this.setupTooltips();
  },
  updated() {
    this.setupTooltips();
  },
  beforeDestroy() {
    this.cleanupTooltips();
  },
  methods: {
    cleanupTooltips() {
      if (this.tooltips.length) {
        this.tooltips.forEach((tooltip) => tooltip.dispose());
        this.tooltips.splice(0);
      }
    },
    setupTooltips() {
      this.cleanupTooltips();

      if (this.outerTooltip && this.$refs.outerRing) {
        this.addTooltip(this.$refs.outerRing, this.outerTooltip);
      }

      if (this.innerTooltip && this.$refs.innerRing) {
        this.addTooltip(this.$refs.innerRing, this.innerTooltip);
      }
    },
    addTooltip(element, content) {
      const tooltip = new D3Tooltip(element, content, this.tooltipOptions);

      this.tooltips.push(tooltip);
    },
  },
};
</script>

<style scoped>
.chart-donut-double .ring-group {
  transition: opacity 0.2s ease, filter 0.2s ease;
}

.chart-donut-double .ring-group:hover {
  filter: brightness(1.1);
}
</style>
